import { cls, isFunction, map } from '@/utils';
import { ForwardedRef, forwardRef, memo, useImperativeHandle } from 'react';

import { getIn } from 'formik';
import { includes, range } from 'ramda';
import { isNilOrEmpty } from 'ramda-adjunct';

import {
	AutocompleteForm,
	CachetForm,
	CheckboxForm,
	DatePickerForm,
	DateSegmentPickerForm,
	InputFileForm,
	RangePickerForm,
	SelectForm,
	Signature,
	TextAreaForm,
	TimePickerForm,
	TriStateCheckbox,
} from '..';
import { ContainerPdf } from '../form-pdf';
import { useLogic } from './define-document-write.logic';
import {
	CheckboxType,
	DefineDocumentWriteModeEnum,
	WrapDocumentProps,
	WrapDocumentRef,
} from './define-document-write.type';

const WrapDocumentWrite = forwardRef(
	(props: WrapDocumentProps, ref: ForwardedRef<WrapDocumentRef>) => {
		const { pages, pageRefs, handlers, data, fileName, showError, hasFile } =
			useLogic(props);

		const { hasEmptyErrors } = props;

		useImperativeHandle(ref, () => ({
			onCancel: handlers.onCancel,
		}));

		return (
			<ContainerPdf
				fileName={fileName}
				pageRefs={pageRefs}
				onSubmit={handlers.onSubmit}
				mode={props.mode}
				hasErrorToDownload={hasEmptyErrors}
				hasFile={hasFile}
			>
				{/* @ts-expect-error: 1234 */}
				{map(pages, (page, index) => (
					<div
						key={index}
						className="container-pdf"
						ref={(el) => (pageRefs.current[index] = el)}
					>
						<img
							alt="pdfImage"
							draggable={false}
							{...page?.background}
							data-source={page?.background?.dataSource}
							key={index}
						/>
						{map(page?.highlightBoxes, (highlightBox, index) => {
							const highlightBoxProps = isFunction(highlightBox)
								? highlightBox(data, index)
								: highlightBox;

							const isEmptyValue =
								(highlightBoxProps?.name &&
									isNilOrEmpty(data?.[highlightBoxProps?.name])) ||
								(highlightBoxProps?.names?.length &&
									highlightBoxProps?.names?.every((key: string) =>
										isNilOrEmpty(data?.[key])
									)) ||
								(highlightBoxProps?.path &&
									isNilOrEmpty(getIn(data, highlightBoxProps?.path)));

							const showErrorHighlightBox =
								!highlightBoxProps?.ignoreValidate &&
								(isEmptyValue || highlightBoxProps?.hasError) &&
								(props.mode ===
									DefineDocumentWriteModeEnum.ISSUE_APPLICATION_DOWNLOAD ||
									showError);

							return (
								<div
									key={index}
									className={cls(
										'highlight-box absolute bg-orange1 h-[23px] opacity-50',
										highlightBoxProps?.className,
										showErrorHighlightBox && 'bg-red-600'
									)}
								/>
							);
						})}

						{map(page?.textAreas, (textArea) => {
							const inputProps = isFunction(textArea)
								? textArea(data)
								: textArea;

							if (isFunction(textArea) && inputProps?.repeat > 1) {
								return (
									<>
										{map(range(0, inputProps?.repeat), (item) => {
											const inputRepeatProps = textArea(data, item);
											return (
												<TextAreaForm
													{...inputRepeatProps}
													value={
														data[inputRepeatProps?.jsonKey]?.[item]?.[
															inputRepeatProps?.name
														]
													}
													onChange={handlers.onChangeInputJson(
														inputRepeatProps?.jsonKey
													)}
												/>
											);
										})}
									</>
								);
							}

							return (
								<TextAreaForm
									{...inputProps}
									onChange={handlers.onChangeInput}
									value={data[inputProps?.name]}
								/>
							);
						})}

						{map(page?.checkboxes, (checkbox, index) => {
							const checkboxProps = isFunction(checkbox)
								? checkbox(data, index)
								: checkbox;

							const isSelectAll =
								checkboxProps.type === CheckboxType.SELECT_ALL;

							if (isSelectAll) {
								const checked =
									data[checkboxProps?.name]?.length ===
									checkboxProps?.options?.length;

								return (
									<CheckboxForm
										className={checkboxProps?.className}
										name={checkboxProps.name as string}
										value={'option'}
										onClick={handlers.onClickCheckboxAll(
											checked,
											checkboxProps?.options,
											checkboxProps?.getResetKey
										)}
										checked={checked}
										iconType={checkboxProps?.iconType}
										iconClassName={checkboxProps?.iconClassName}
										key={`${checkboxProps?.name}-${index}`}
										disabled={checkboxProps?.disabled}
									/>
								);
							}
							return (
								<>
									{map(checkboxProps?.options, (option, idx) => {
										const isMultiple =
											checkboxProps?.type === CheckboxType.MULTIPLE;
										const checked = isMultiple
											? includes(option, data[checkboxProps?.name])
											: data[checkboxProps?.name] === option;
										return (
											<CheckboxForm
												className={checkboxProps?.classNames?.[idx]}
												name={checkboxProps.name as string}
												value={option as string}
												onClick={handlers.onClickCheckbox(
													checked,
													checkboxProps?.type,
													checkboxProps?.getResetKey
												)}
												checked={checked}
												iconType={checkboxProps?.iconType}
												iconClassName={checkboxProps?.iconClassName}
												key={`${checkboxProps?.name}-${option}-${checkboxProps?.classNames?.[idx]}-${idx}`}
												disabled={checkboxProps?.disabled}
											/>
										);
									})}
								</>
							);
						})}

						{map(page?.triStateCheckboxes, (triStateCheckbox) => {
							return (
								<>
									{map(triStateCheckbox?.options, (value, index) => {
										return (
											<TriStateCheckbox
												value={data[triStateCheckbox?.name]?.[index]}
												onClick={handlers.onClickTriStateCheckbox(index)}
												name={triStateCheckbox.name}
												className={triStateCheckbox?.classNames?.[index]}
												key={`${triStateCheckbox?.name}-${index}`}
											/>
										);
									})}
								</>
							);
						})}

						{map(page?.signatures, (signature) => {
							const signatureProps = isFunction(signature)
								? signature(data)
								: signature;

							if (isFunction(signature) && signatureProps?.repeat > 1) {
								return (
									<>
										{map(range(0, signatureProps?.repeat), (item) => {
											const signatureRepeatProps = signature(data, item);

											return (
												<Signature
													{...signatureRepeatProps}
													dataUrl={
														data[signatureRepeatProps?.jsonKey]?.[item]?.[
															signatureRepeatProps?.name
														]
													}
													onSubmit={handlers.onSubmitSignatureJson(
														signatureRepeatProps?.jsonKey,
														signatureRepeatProps?.name,
														item
													)}
												/>
											);
										})}
									</>
								);
							}

							return (
								<Signature
									{...signatureProps}
									dataUrl={data[signatureProps?.name]}
									onSubmit={handlers?.onSubmitSignature}
								/>
							);
						})}

						{map(page?.datePickers, (datePicker) => {
							const datePickerProps = isFunction(datePicker)
								? datePicker(data)
								: datePicker;

							// `segmentedInputs` đổi sang loại 3 ô nhập riêng (년/월/일),
							// các prop còn lại dùng chung nên giữ nguyên cách truyền.
							//
							// `segmentedInputs`가 켜지면 연/월/일 세 칸 입력 컴포넌트로
							// 전환되며 나머지 prop은 동일하게 전달됩니다.
							const DatePickerComponent = datePickerProps?.segmentedInputs
								? DateSegmentPickerForm
								: DatePickerForm;

							if (isFunction(datePicker) && datePickerProps?.repeat > 1) {
								return (
									<>
										{map(range(0, datePickerProps?.repeat), (item) => {
											const datePickerRepeatProps = datePicker(data, item);

											return (
												<DatePickerComponent
													{...datePickerRepeatProps}
													value={
														data[datePickerRepeatProps?.jsonKey]?.[item]?.[
															datePickerRepeatProps?.name
														]
													}
													onChange={handlers.onChangeDateJson(
														datePickerRepeatProps?.jsonKey,
														datePickerRepeatProps?.name,
														item
													)}
												/>
											);
										})}
									</>
								);
							}

							return (
								<DatePickerComponent
									{...datePickerProps}
									value={data[datePickerProps?.name]}
									onChange={handlers?.onChangeDate}
								/>
							);
						})}

						{map(page?.selects, (select) => {
							const selectProps = isFunction(select) ? select(data) : select;

							if (isFunction(select) && selectProps?.repeat > 1) {
								return (
									<>
										{map(range(0, selectProps?.repeat), (item) => {
											const selectRepeatProps = select(data, item);
											const value =
												data[selectRepeatProps?.jsonKey]?.[item]?.[
													selectRepeatProps?.name
												];

											return (
												<SelectForm
													{...selectRepeatProps}
													value={value}
													onChange={handlers.onChangeInputJson(
														selectRepeatProps?.jsonKey,
														selectRepeatProps?.resetKeys
													)}
												/>
											);
										})}
									</>
								);
							}

							return (
								<SelectForm
									{...selectProps}
									value={data[selectProps?.name]}
									onChange={handlers.onChangeInput}
								/>
							);
						})}

						{map(page?.inputFiles, (inputFile) => (
							<InputFileForm
								{...inputFile}
								dataImg={data[inputFile?.name]}
								onChange={handlers?.onChangeInputFile}
							/>
						))}
						{map(page?.rangePickers, (rangePicker) => (
							<RangePickerForm
								{...rangePicker}
								startDate={data[rangePicker?.name?.[0]]}
								endDate={data[rangePicker?.name?.[1]]}
								name={rangePicker?.name}
								onChange={handlers?.onChangeRangePicker}
							/>
						))}
						{map(page?.timePickers, (timePicker) => {
							const timePickerProps = isFunction(timePicker)
								? timePicker(data)
								: timePicker;
							return (
								<TimePickerForm
									{...timePickerProps}
									value={data?.[timePickerProps?.name]}
									name={timePickerProps?.name}
									onChange={handlers?.onChangeTimePicker}
								/>
							);
						})}

						{map(page?.autocompletes, (autocomplete) => {
							const autocompleteProps = isFunction(autocomplete)
								? autocomplete(data)
								: autocomplete;

							if (isFunction(autocomplete) && autocompleteProps?.repeat > 1) {
								return (
									<>
										{map(range(0, autocompleteProps?.repeat), (item) => {
											const autocompleteRepeatProps = autocomplete(data, item);

											return (
												<AutocompleteForm
													{...autocompleteRepeatProps}
													options={autocompleteRepeatProps?.options?.[item]}
													value={
														data[autocompleteRepeatProps?.jsonKey]?.[item]?.[
															autocompleteRepeatProps?.name
														]
													}
													onChange={handlers.onChangeAutocompleteJson(
														autocompleteRepeatProps?.jsonKey
													)}
												/>
											);
										})}
									</>
								);
							}

							return (
								<AutocompleteForm
									{...autocompleteProps}
									onChange={handlers.onChangeInput}
									value={data[autocompleteProps?.name]}
								/>
							);
						})}

						{map(page?.cachetForms, (cachetForm) => (
							<CachetForm {...cachetForm} />
						))}
					</div>
				))}
			</ContainerPdf>
		);
	}
);

WrapDocumentWrite.displayName = 'WrapDocumentWrite';

export default memo(WrapDocumentWrite);
